import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ProgressBarState, SyncProvider } from '../../../models/integration.model';
import { FuseAnimations } from '@fuse/animations';
import { IntegrationPermissionService } from 'app/services/Integration/integration-permission.service';
import { Observable, combineLatest, from, of } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Sync Progress Bar Component
 * Displays real-time progress for Unifier sync operations
 * Following existing component patterns in the codebase
 */
@Component({
  selector: 'app-sync-progress-bar',
  templateUrl: './sync-progress-bar.component.html',
  styleUrls: ['./sync-progress-bar.component.scss'],
  animations: FuseAnimations,
})
export class SyncProgressBarComponent implements OnInit, OnChanges {
  @Input() progressState: ProgressBarState;
  @Input() provider: SyncProvider = 'projects';
  @Output() closed = new EventEmitter<void>();

  isVisible$: Observable<boolean>;
  private permission$: Observable<boolean>;

  constructor(
    private _transloco: TranslocoService,
    private _permissionService: IntegrationPermissionService,
  ) {}

  ngOnInit(): void {
    // Create the permission observable once by converting Promise to Observable
    this.permission$ = of(this._permissionService.canSyncProvider(this.provider));
    this.updateVisibility();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update visibility when progressState changes
    if (changes['progressState'] && this.permission$) {
      this.updateVisibility();
    }
  }

  private updateVisibility(): void {
    // Create observable for visibility that combines progress state and permissions
    this.isVisible$ = combineLatest([this.permission$]).pipe(
      map(([canSync]) => {
        return this.progressState?.isVisible && canSync;
      }),
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get progress bar CSS class based on status
   */
  getProgressBarClass(): string {
    if (!this.progressState) return 'progress-default';

    switch (this.progressState.status) {
      case 'processing':
        return 'progress-processing';
      case 'completed':
        return 'progress-completed';
      case 'failed':
        return 'progress-failed';
      default:
        return 'progress-default';
    }
  }

  /**
   * Get progress message with counts
   */
  getProgressMessage(): string {
    if (!this.progressState) return '...';

    if (this._transloco.getActiveLang() === 'ar') {
      return this.progressState.arabicMessage;
    }

    return this.progressState.message;
  }

  /**
   * Get status icon based on progress status
   */
  getStatusIcon(): string {
    if (!this.progressState) return '';

    switch (this.progressState.status) {
      case 'processing':
        return 'heroicons_solid:refresh';
      case 'completed':
        return 'heroicons_solid:check-circle';
      case 'failed':
        return 'heroicons_solid:x-circle';
      default:
        return 'heroicons_solid:refresh';
    }
  }

  /**
   * Get status color class
   */
  getStatusColorClass(): string {
    if (!this.progressState) return 'text-secondary';

    switch (this.progressState.status) {
      case 'processing':
        return 'text-primary';
      case 'completed':
        return 'text-success';
      case 'failed':
        return 'text-error';
      default:
        return 'text-secondary';
    }
  }

  /**
   * Check if the sync has finished: completed, failed or 100%. A finished run shows its
   * outcome instead of a progress bar frozen at a partial percentage.
   */
  isSyncCompleted(): boolean {
    if (!this.progressState) return false;
    return (
      this.progressState.status === 'completed' ||
      this.progressState.status === 'failed' ||
      this.progressState.percentage >= 100
    );
  }

  isSyncFailed(): boolean {
    return this.progressState?.status === 'failed';
  }

  /**
   * Handle close button click
   */
  onClose(): void {
    this.closed.emit();
  }
}
